import {
  SvgIconComponent,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { ReactNode } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { STREAM_UI_CONFIG } from '../../consts';
import { focusTrackMessages } from './focus-track-messages';

type FocusTrackHeaderProps = {
  label: MessageDescriptor;
  /** Small leading icon shown next to the track label. */
  icon: SvgIconComponent;
  /** Whether the track body is currently shown. */
  visible: boolean;
  /** Toggles track body visibility (persisted audit setting). */
  onToggle: () => void;
  /** Extra controls rendered left of the visibility toggle (e.g. density). */
  actions?: ReactNode;
};

/**
 * Banded caption row above a focus track (v2 NleTrackHeader foreshadow): filled
 * band + eye toggle. Collapsing hides the lane but keeps this header reachable.
 * z-index keeps the band above overflowing track content (e.g. wide topic pills).
 */
export function FocusTrackHeader({
  label,
  icon: Icon,
  visible,
  onToggle,
  actions,
}: FocusTrackHeaderProps) {
  const { $t } = useIntl();

  return (
    <Box
      sx={{
        position: 'relative',
        zIndex: 5,
        height: STREAM_UI_CONFIG.focusTrackHeaderHeight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 0.5,
        px: 0.75,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'action.hover',
        flexShrink: 0,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
        <Icon sx={{ fontSize: 12, color: 'text.disabled', flexShrink: 0 }} />
        <Typography variant="caption" color="text.secondary" noWrap>
          {$t(label)}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
        {actions}
        <Tooltip title={visible ? $t(focusTrackMessages.hide) : $t(focusTrackMessages.show)}>
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              onToggle();
            }}
            sx={{
              p: 0.25,
              width: 22,
              height: 22,
              color: 'text.secondary',
            }}
          >
            {visible ? (
              <VisibilityIcon sx={{ fontSize: 18 }} />
            ) : (
              <VisibilityOffIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
